import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { ServerError, ServerParseError } from '@apollo/client/core';
import { ShowNotification } from '@macs-frontend/features/shared';
import { formatServerFormErrors } from '@macs-frontend/features/shared/models/form-error-response';
import { StringUtils } from '@macs-frontend/features/shared/utils/string';
import { Store } from '@ngrx/store';
import { captureException } from '@sentry/browser';
import { GraphQLError, GraphQLFormattedError } from 'graphql';
import { BaseStore } from './base.store';

@Injectable({
  providedIn: 'root',
})
export class NotificationStore extends BaseStore {
  apolloErrors?: GraphQLError[];

  showServerValidationErrors = (err: HttpErrorResponse) => {
    if (err.error?.errors) {
      const errors = formatServerFormErrors({ errors: err.error.errors });

      this.store$.dispatch(
        new ShowNotification({
          title: 'Heads up!',
          error: {
            title: '',
            items:
              err.error.errors.errors ||
              Object.keys(errors).flatMap(key =>
                errors[key].map(
                  value => `${StringUtils.humanizeTextCode(key)} ➤ ${value}`,
                ),
              ),
          },
        }),
      );
    }
  };

  showApolloErrorsWithMacrotask(errors?: readonly GraphQLFormattedError[]) {
    this.apolloErrors = errors as GraphQLError[] | undefined;
    setTimeout(() => {
      if (this.apolloErrors) {
        this.showApolloErrors(this.apolloErrors);
      }
    });
  }

  clearApolloErrors() {
    this.apolloErrors = undefined;
  }

  showApolloErrors = (
    errors?: readonly GraphQLError[],
    defaultMessage = 'Internal server error.',
  ) => {
    errors?.forEach(err => {
      captureException(err);
      console.error(err);
    });

    this.store$.dispatch(
      new ShowNotification({
        title: 'Heads up!',
        error: {
          title: '',
          items: errors?.map(error =>
            'debugMessage' in error ? (error as any).debugMessage : error.message,
          ) || [defaultMessage],
        },
      }),
    );
  };

  showApolloNetworkError = (error: Error | ServerError | ServerParseError) => {
    captureException(error);
    console.error(error);

    this.store$.dispatch(
      new ShowNotification({
        title: 'Heads up!',
        error: {
          title: '',
          items: [`[Network Error] ${error.message}`],
        },
      }),
    );
  };

  constructor(readonly store$: Store<any>) {
    super();
  }
}
